import { defineStore } from "pinia";
import { useCartStore } from "./cart";
import { useEventsStore } from "./events";
import { useTicketCartStore } from "./ticketCart";
import { useSnackbar } from "./snackbar";
import reserveTickets from "@/api/requests/reserveTickets";
import { ref, Ref } from "vue";
import Reservation from "@/types/Reservation";
import { useTimer } from "@/composables/useTimer";
import { DateTime } from "luxon";
import PixPayment from "@/types/PixPayment";
import CreditCardPayment from "@/types/CreditCardPayment";
import CheckoutResult from "@/types/CheckoutResult";
import PaymentMethod from "@/types/PaymentMethod";
import PaymentData from "@/types/PaymentData";
import { generateTickets } from "@/utils/checkout";
import Order from "@/types/Order";
import createOrder from "@/api/requests/createOrder";
import payOrder from "@/api/requests/payOrder";
import { useAffiliate } from "@/composables/useAffiliate";
import InstallmentPlan from "@/types/InstallmentPlan";

export const useCheckoutStore = defineStore("checkout", () => {
  const reservation: Ref<Reservation | null> = ref(null);

  const transaction: Ref<PixPayment | CreditCardPayment | null> = ref(null);

  const order: Ref<Order | null> = ref(null);

  /** The Installment plan the buyer chose for paying the Order by card. */
  const plan: Ref<InstallmentPlan | null> = ref(null);

  const timer = useTimer();

  const cart = useCartStore();

  /** The Reservation ran out on the API, so the buyer starts over from the first step with nothing picked. */
  function expire(): void {
    cart.clear();
    cart.tickets = [];
    useTicketCartStore().clear();
    cart.stepper.reset();
    useSnackbar().show("O tempo para concluir sua compra acabou. Escolha seus ingressos novamente.", "warning");
  }

  function reset() {
    reservation.value = null;
    transaction.value = null;
    plan.value = null;
    timer.reset();
  }

  async function createReservation(token: string): Promise<Reservation | null> {
    const event = useEventsStore().selected;

    if (event === null) return null;

    const response = await reserveTickets({
      event_id: event.id,
      sectors: cart.sectors,
      token,
    });

    reservation.value = response;

    timer.start({
      expiresAt: DateTime.now().plus({ minutes: 15 }),
      onExpire: expire,
    });

    return response;
  }

  async function reserve(token: string): Promise<void> {
    const response = await createReservation(token);

    if (response === null) return;

    cart.tickets = generateTickets(cart.sectors, response);
  }

  /**
   * Creates the reservation for the Cart of the mapped purchase: the type amounts go into the cart's sectors, which
   * the reservation and the summary read, and the tickets are built from the Cart, each with its own seat.
   */
  async function reserveCart(token: string): Promise<void> {
    const ticketCart = useTicketCartStore();

    cart.sectors = ticketCart.toReservation(cart.sectors);

    const response = await createReservation(token);

    if (response === null) return;

    cart.tickets = ticketCart.toTickets();
  }

  async function create(): Promise<Order> {
    if (reservation.value === null) {
      throw new Error("Nenhuma reserva encontrada para criar o pedido");
    }

    const event = useEventsStore().selected;

    if (event === null) {
      throw new Error("Evento não selecionado");
    }

    transaction.value = null;

    const { tag } = useAffiliate();

    const response = await createOrder(reservation.value, cart.tickets, cart.coupons.list, tag.value);

    order.value = response.data;

    // Its seats belong to the order now, a later visit starts a new Cart
    useTicketCartStore().clear();

    return response.data;
  }

  async function pay(method: PaymentMethod, data: PaymentData): Promise<CheckoutResult> {
    if (order.value === null) {
      throw new Error("Nenhuma compra encontrada para processar o pagamento");
    }

    const event = useEventsStore().selected;

    if (event === null) {
      throw new Error("Evento não selecionado");
    }

    return await payOrder(order.value, data, method);
  }

  return {
    reservation,
    transaction,
    reset,
    reserve,
    reserveCart,
    order,
    plan,
    create,
    pay,
    timer,
  };
}, { persist: true });
